import type { ControlDesk, Regions } from "types/region"

export const getRegionsByControlDesk = (controlDesk: ControlDesk): Regions => {
  /**
   * TODO
   * figure out what to do with controlDesks that don't have
   * a corresponding region in our database
   *
   * currently only sussex, kent, wessex, NWR exist in the DB
   */
  switch (controlDesk) {
    case "Sussex-Kent":
      return ["Sussex", "Kent"]
    // all other control desks represent a single region
    default:
      return [controlDesk]
  }
}

/** Union of the regions of several desks (ART-3177 multi-desk). */
export const getRegionsByControlDesks = (controlDesks: readonly ControlDesk[]): Regions => [
  ...new Set(controlDesks.flatMap((desk) => getRegionsByControlDesk(desk))),
]

/**
 * The desks a user may tick in the top bar (ART-3177):
 * - "ALL"/"???" accounts: every desk (as before, when they could pick any one);
 * - a user with a per-user override (super-admin, Customer Configuration):
 *   their own desk plus the override's desks — replacing the desk's list;
 * - everyone else: their own desk plus the desks a super-admin has made
 *   visible to it (Customer Configuration → desk visibility), in that order.
 * Empty until the user's desk is known (unless an override gives them desks).
 */
export const getAllowedDesks = ({
  homeDesk,
  allDesks,
  everyDesk,
  deskVisibility,
  userDeskVisibility,
}: {
  homeDesk: ControlDesk | null
  allDesks: boolean
  everyDesk: readonly ControlDesk[]
  deskVisibility?: Partial<Record<string, string[]>>
  userDeskVisibility?: readonly string[]
}): ControlDesk[] => {
  if (allDesks) return [...everyDesk]
  const override = Array.isArray(userDeskVisibility) ? userDeskVisibility : null
  if (!homeDesk && !override) return []
  const visible = (override ?? (homeDesk ? deskVisibility?.[homeDesk] : undefined) ?? []).filter(
    (desk): desk is ControlDesk => desk !== homeDesk && (everyDesk as readonly string[]).includes(desk),
  )
  return [...(homeDesk ? [homeDesk] : []), ...new Set(visible)]
}

/**
 * A usable selection: the candidate's desks that are allowed, in allowed
 * order (so the home desk leads and stays the "primary" desk), falling back
 * to `fallback` (or the first allowed desk) when nothing valid is left.
 */
export const resolveDeskSelection = (
  candidate: readonly string[] | null | undefined,
  allowed: readonly ControlDesk[],
  fallback: ControlDesk,
): ControlDesk[] => {
  if (allowed.length === 0) return [fallback]
  const picked = allowed.filter((desk) => candidate?.includes(desk))
  if (picked.length > 0) return picked
  return [allowed.includes(fallback) ? fallback : allowed[0]]
}

/** The control desk a region belongs to (Sussex and Kent share one desk). */
export const getControlDeskByRegion = (region: string): ControlDesk =>
  (region === "Sussex" || region === "Kent" ? "Sussex-Kent" : region) as ControlDesk
